/* STEP: Account Code. Code/display/prozo-id/op-email + linked service types echo,
   plus the backend-MANDATORY courier attributes (weights, dimensions, SLA, pickup/
   delivery, tracking) in a collapsible section — every attribute must be entered
   explicitly (no silent business defaults; these are facts from the courier's terms).
   Prozo ID auto-suggested from the courier code. */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {Field, Footer, scrollToFirstError} from './fieldKit'
import {defaultAttrs} from '../data'

/* numeric attributes the backend requires non-empty */
const NUM_KEYS = [
  'minChargeableWeight',
  'maxWeightSinglePiece',
  'maxWeightPerShipment',
  'volumetricDivisor',
  'maxLength',
  'maxWidth',
  'maxHeight',
  'maxGirth',
  'maxLwhSum',
  'maxPiecesPerShipment',
  'maxDeliveryAttempts',
  'forwardSlaMetroDays',
  'forwardSlaNonMetroDays',
]

const YN = ['Yes', 'No']

/* select attributes the backend requires — must be consciously chosen, no defaults */
const SELECT_KEYS = [
  'chargeableWeightRule',
  'multiPieceAllowed',
  'fragileHandlingAvailable',
  'specialPackagingRequired',
  'metroServiceable',
  'nddEligible',
  'sddEligible',
  'pickupDays',
  'deliveryDays',
  'realTimeTracking',
  'trackingType',
  'integration',
]

export default function StepCode({W, dispatch, goBack, goNext}) {
  const D = W.D
  const attrs = D.attrs || defaultAttrs()
  const [inv, setInv] = useState({})
  const [attrsOpen, setAttrsOpen] = useState(false)

  const set = (key, value) => {
    dispatch({type: 'FIELD', slice: 'D', key, value})
    if (inv[key]) setInv((p) => ({...p, [key]: false}))
  }
  const setAttr = (key, value) => {
    dispatch({type: 'FIELD', slice: 'D', key: 'attrs', value: {...attrs, [key]: value}})
    if (inv[key]) setInv((p) => ({...p, [key]: false}))
  }
  const setCode = (value) => {
    const patch = {code: value}
    if (!D.prozo || /_PRZ_1$/.test(D.prozo)) patch.prozo = value ? value + '_PRZ_1' : ''
    dispatch({type: 'PATCH', slice: 'D', value: patch})
    if (inv.code) setInv((p) => ({...p, code: false}))
  }

  /* default the proship integration type from the parent's integration method:
     CLICKPOST → CP, MANUAL → NONAPI, DIRECT_API → CP (editable below) */
  useEffect(() => {
    if (!D.attrs) {
      const a = defaultAttrs()
      // derived (not defaulted): proship integration type follows the parent's method, editable below
      if (W.parentRef && W.parentRef.integrationMethod) {
        a.integration = W.parentRef.integrationMethod === 'MANUAL' ? 'NONAPI' : 'CP'
      }
      dispatch({type: 'FIELD', slice: 'D', key: 'attrs', value: a})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const parentName = W.parentMode === 'new' ? W.A.name || '(new parent)' : W.D.parentId || '—'

  const onNext = () => {
    const e = {}
    ;['code', 'display', 'prozo', 'email'].forEach((k) => {
      if (!String(D[k] || '').trim()) e[k] = true
    })
    if (!/^[0-9]{10,15}$/.test(String(D.contactNo || '').trim())) e.contactNo = true
    /* CP Id (Clickpost) — only for the existing-parent flow (new parent collects it on
       the Click Post / Direct Integration step instead) */
    if (W.parentMode !== 'new' && !String(D.cpId || '').trim()) e.cpId = true
    NUM_KEYS.forEach((k) => {
      if (String(attrs[k] == null ? '' : attrs[k]).trim() === '') e[k] = true
    })
    SELECT_KEYS.forEach((k) => {
      if (!String(attrs[k] || '').trim()) e[k] = true
    })
    if (!String(attrs.pickupCutoffTime || '').trim()) e.pickupCutoffTime = true
    if (!String(attrs.onboardedDate || '').trim()) e.onboardedDate = true
    if (!String(attrs.remarks || '').trim()) e.remarks = true // backend @NotBlank
    setInv(e)
    scrollToFirstError()
    if (Object.keys(e).length) {
      const attrErr = NUM_KEYS.some((k) => e[k]) || SELECT_KEYS.some((k) => e[k]) || e.pickupCutoffTime || e.onboardedDate || e.remarks
      if (attrErr) setAttrsOpen(true) // reveal the attributes accordion so its invalid fields are visible before scrolling
      return
    }
    goNext()
  }

  const grid2 = {display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px'}

  return (
    <div className='card card-pad' style={{maxWidth: 1400}}>
      <h3 style={{margin: '0 0 12px'}}>Account Code Setup</h3>
      <div className='warnbox info' style={{marginBottom: 16}}>
        <InfoOutlinedIcon style={{width: 16, height: 16}} />
        <div>
          Parent: <b>{parentName}</b> &nbsp;·&nbsp; Direction: <b>{W.S.direction || '—'}</b> &nbsp;·&nbsp; Mode: <b>{W.S.mode || '—'}</b>
        </div>
      </div>
      <div style={grid2}>
        <Field label='CP Account Code' value={D.code} onChange={setCode} ph='Shadowfax_NDD' invalid={inv.code} help='Exact account code used in ProShip and the courier API (cpAccountCode). Unique, case-sensitive, no spaces.' />
        <Field label='Display name' value={D.display} onChange={(v) => set('display', v)} ph='Shadowfax Next-Day' invalid={inv.display} help='Human-readable name shown in reports.' />
      </div>
      <div style={grid2}>
        <Field label='Prozo courier ID' value={D.prozo} onChange={(v) => set('prozo', v)} ph='Shadowfax_NDD_PRZ_1' invalid={inv.prozo} help='Internal Prozo ID. Auto-suggested from the courier code.' />
        <Field label='Operational email' value={D.email} onChange={(v) => set('email', v)} ph='ops@courier.com' invalid={inv.email} help='Courier operational inbox for this code.' />
      </div>
      <div style={grid2}>
        <Field label='Contact number' value={D.contactNo} onChange={(v) => set('contactNo', v)} ph='10–15 digits' invalid={inv.contactNo} help='Courier ops contact for this account.' />
        {W.parentMode !== 'new' ? (
          <Field label='CP Id (Clickpost)' type='number' value={D.cpId} onChange={(v) => set('cpId', v)} ph='e.g. 11' invalid={inv.cpId} help='Clickpost courier id for this account.' />
        ) : null}
      </div>

      <div className='field'>
        <label>Linked service types</label>
        <div style={{border: '1px solid var(--border)', borderRadius: 6, padding: 10, display: 'flex', flexWrap: 'wrap'}}>
          {D.serviceTypes.length ? (
            D.serviceTypes.map((c) => (
              <span key={c} className='tag tag-mono' style={{margin: '0 4px 4px 0'}}>
                {c}
              </span>
            ))
          ) : (
            <span className='muted'>none selected</span>
          )}
        </div>
        <div className='help' style={{display: 'flex', alignItems: 'center', gap: 4}}>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          Chosen in the previous step. Go back to change them.
        </div>
      </div>

      {/* ── Courier attributes (backend-mandatory) ─────────────────────────── */}
      <div style={{border: '1px solid var(--border)', borderRadius: 8, marginTop: 4}}>
        <button
          type='button'
          className='btn'
          style={{width: '100%', justifyContent: 'flex-start', border: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px'}}
          onClick={() => setAttrsOpen((o) => !o)}
        >
          {attrsOpen ? <ExpandMoreIcon style={{width: 16, height: 16}} /> : <ChevronRightIcon style={{width: 16, height: 16}} />}
          <b>Courier attributes</b>
          <span className='muted' style={{fontSize: 12, fontWeight: 400}}>
            — weights, dimensions, SLA, pickup &amp; tracking (required)
          </span>
        </button>
        {attrsOpen ? (
          <div style={{padding: '4px 14px 12px'}}>
            <div className='seclabel' style={{margin: '6px 0 8px'}}>
              Weights
            </div>
            <div style={grid2}>
              {/* Kilograms — the backend stores these in kg (DirectCourierService.putGrams multiplies by 1000),
                  and the Courier Codes drawer / bulk template label them in kg. They were labelled "gm" here,
                  so wizard-created codes carried weights 1000x too large (e.g. min_chargeable_weight 500). */}
              <Field label='Min chargeable weight (kg)' type='number' value={String(attrs.minChargeableWeight)} onChange={(v) => setAttr('minChargeableWeight', v)} ph='0.5' invalid={inv.minChargeableWeight} />
              <Field label='Max weight / single piece (kg)' type='number' value={String(attrs.maxWeightSinglePiece)} onChange={(v) => setAttr('maxWeightSinglePiece', v)} ph='30' invalid={inv.maxWeightSinglePiece} />
              <Field label='Max weight / shipment (kg)' type='number' value={String(attrs.maxWeightPerShipment)} onChange={(v) => setAttr('maxWeightPerShipment', v)} ph='30' invalid={inv.maxWeightPerShipment} />
              <Field label='Volumetric divisor' type='number' value={String(attrs.volumetricDivisor)} onChange={(v) => setAttr('volumetricDivisor', v)} ph='5000' invalid={inv.volumetricDivisor} />
              <Field label='Chargeable weight rule' type='select' options={['DW', 'VOL', 'HIGHER']} value={attrs.chargeableWeightRule} onChange={(v) => setAttr('chargeableWeightRule', v)} />
            </div>

            <div className='seclabel' style={{margin: '6px 0 8px'}}>
              Dimensions
            </div>
            <div style={grid2}>
              <Field label='Max length (cm)' type='number' value={String(attrs.maxLength)} onChange={(v) => setAttr('maxLength', v)} invalid={inv.maxLength} />
              <Field label='Max width (cm)' type='number' value={String(attrs.maxWidth)} onChange={(v) => setAttr('maxWidth', v)} invalid={inv.maxWidth} />
              <Field label='Max height (cm)' type='number' value={String(attrs.maxHeight)} onChange={(v) => setAttr('maxHeight', v)} invalid={inv.maxHeight} />
              <Field label='Max girth (cm)' type='number' value={String(attrs.maxGirth)} onChange={(v) => setAttr('maxGirth', v)} invalid={inv.maxGirth} />
              <Field label='Max L+W+H sum (cm)' type='number' value={String(attrs.maxLwhSum)} onChange={(v) => setAttr('maxLwhSum', v)} invalid={inv.maxLwhSum} />
            </div>

            <div className='seclabel' style={{margin: '6px 0 8px'}}>
              Handling
            </div>
            <div style={grid2}>
              <Field label='Multi-piece allowed' type='select' options={YN} value={attrs.multiPieceAllowed} onChange={(v) => setAttr('multiPieceAllowed', v)} />
              <Field label='Max pieces / shipment' type='number' value={String(attrs.maxPiecesPerShipment)} onChange={(v) => setAttr('maxPiecesPerShipment', v)} invalid={inv.maxPiecesPerShipment} />
              <Field label='Fragile handling available' type='select' options={YN} value={attrs.fragileHandlingAvailable} onChange={(v) => setAttr('fragileHandlingAvailable', v)} />
              <Field label='Special packaging required' type='select' options={YN} value={attrs.specialPackagingRequired} onChange={(v) => setAttr('specialPackagingRequired', v)} />
            </div>
            <Field label='Remarks' type='textarea' value={attrs.remarks} onChange={(v) => setAttr('remarks', v)} ph='Handling notes for ops (required by backend)' invalid={inv.remarks} />

            <div className='seclabel' style={{margin: '6px 0 8px'}}>
              Coverage &amp; SLA
            </div>
            <div style={grid2}>
              <Field label='Metro serviceable' type='select' options={YN} value={attrs.metroServiceable} onChange={(v) => setAttr('metroServiceable', v)} />
              <Field label='NDD eligible' type='select' options={YN} value={attrs.nddEligible} onChange={(v) => setAttr('nddEligible', v)} />
              <Field label='SDD eligible' type='select' options={YN} value={attrs.sddEligible} onChange={(v) => setAttr('sddEligible', v)} />
              <Field label='Forward SLA — Metro (days)' type='number' value={String(attrs.forwardSlaMetroDays)} onChange={(v) => setAttr('forwardSlaMetroDays', v)} invalid={inv.forwardSlaMetroDays} />
              <Field label='Forward SLA — Non-metro (days)' type='number' value={String(attrs.forwardSlaNonMetroDays)} onChange={(v) => setAttr('forwardSlaNonMetroDays', v)} invalid={inv.forwardSlaNonMetroDays} />
            </div>

            <div className='seclabel' style={{margin: '6px 0 8px'}}>
              Pickup &amp; delivery
            </div>
            <div style={grid2}>
              <Field label='Pickup cutoff time' type='time' value={attrs.pickupCutoffTime} onChange={(v) => setAttr('pickupCutoffTime', v)} invalid={inv.pickupCutoffTime} help='HH:mm — last slot for same-day pickup.' />
              <Field label='Pickup days' type='select' options={['MON_SAT', 'MON_SUN']} value={attrs.pickupDays} onChange={(v) => setAttr('pickupDays', v)} />
              <Field label='Max delivery attempts' type='number' value={String(attrs.maxDeliveryAttempts)} onChange={(v) => setAttr('maxDeliveryAttempts', v)} invalid={inv.maxDeliveryAttempts} />
              <Field label='Delivery days' type='select' options={['MON_SAT', 'MON_SUN']} value={attrs.deliveryDays} onChange={(v) => setAttr('deliveryDays', v)} />
            </div>

            <div className='seclabel' style={{margin: '6px 0 8px'}}>
              Tracking &amp; integration
            </div>
            <div style={grid2}>
              <Field label='Real-time tracking' type='select' options={YN} value={attrs.realTimeTracking} onChange={(v) => setAttr('realTimeTracking', v)} />
              <Field label='Tracking type' type='select' options={['API', 'WEB', 'BOTH']} value={attrs.trackingType} onChange={(v) => setAttr('trackingType', v)} />
              <Field label='Proship integration' type='select' options={['CP', 'NONAPI', 'ESZ']} value={attrs.integration} onChange={(v) => setAttr('integration', v)} help='CP = via Clickpost, NONAPI = manual, ESZ = Eshipz. Defaulted from the parent integration method.' />
              <Field label='Supported shipment type' type='select' options={['B2C', 'B2B']} value={attrs.supportedShipmentType} onChange={(v) => setAttr('supportedShipmentType', v)} />
              <Field label='Onboarded date' type='date' value={attrs.onboardedDate} onChange={(v) => setAttr('onboardedDate', v)} invalid={inv.onboardedDate} />
            </div>
          </div>
        ) : null}
      </div>

      <Footer step={W.step} onBack={goBack} onNext={onNext} />
    </div>
  )
}

StepCode.propTypes = {
  W: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
}
